import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, CardContent, CircularProgress, Grid, InputAdornment, Slider, TextField, Typography } from '@mui/material';
import { useAppContext } from '../context/AppContext';
import { wealthService, apiErrorMessage } from '../utils/api';
import { queueFamilySave } from './wealth/familySave';

const MAX_MEMBERS = 3;
const PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
const MOBILE_REGEX = /^[6-9][0-9]{9}$/;
const NAME_REGEX = /^[A-Za-z][A-Za-z .'-]{0,99}$/;

const toTenDigits = (value) => {
  const digits = String(value || '').replace(/\D/g, '');
  return digits.length === 12 && digits.startsWith('91') ? digits.slice(2) : digits.slice(0, 10);
};
const cleanPan = (value) => value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 10);

/** Error text per member field ('' when fine). ownMobiles: the Self mobile entered on this card. */
function validate(members, ownMobiles) {
  return members.map((m, i) => {
    const others = members.slice(0, i).map((o) => o.mobile);
    const name = NAME_REGEX.test(m.name.trim()) ? '' : 'Enter a name';
    let mobile = '';
    if (!MOBILE_REGEX.test(m.mobile)) mobile = 'Enter a valid 10-digit mobile number';
    else if (ownMobiles.includes(m.mobile) || others.includes(m.mobile)) mobile = 'This number is already added';
    // A member saved earlier with the same mobile may leave PAN empty (it is asked again on Connect)
    const panOptional = !m.pan && m.savedMobile && m.savedMobile === m.mobile;
    const pan = panOptional || PAN_REGEX.test(m.pan) ? '' : 'Enter a valid PAN (e.g. ABCDE1234F)';
    return { name, mobile, pan };
  });
}

/**
 * Fetch journey, right after Risk Profile: the user (Self, prefilled) and up to 3 family
 * members whose investments are fetched and added up. Name and mobile are saved; PAN is kept
 * server-side for 24h prefill only (not in the DB or browser storage).
 */
const FamilyMembers = () => {
  const { state, actions } = useAppContext();
  const [members, setMembers] = useState([]);
  const [self, setSelf] = useState({ name: '', mobile: '', pan: '' });
  const [selfTouched, setSelfTouched] = useState({});
  const [touched, setTouched] = useState({}); // "index:field" -> true
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const latestRef = useRef({ members: [], self: {}, valid: false, loaded: false });

  useEffect(() => {
    wealthService.getFamily()
      .then((res) => {
        const me = res.self || {};
        setSelf({
          name: me.name || state.clientName || '',
          mobile: toTenDigits(me.mobile || state.phoneNumber),
          pan: me.pan || res.self_pan || '',
        });
        setMembers((res.members || []).map((m) => ({
          name: m.name || '', mobile: m.mobile || '', pan: m.pan || '', savedMobile: m.mobile || '',
        })));
      })
      .catch((err) => setLoadError(apiErrorMessage(err, 'Could not load family members.')))
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- load once; login name/phone are fallbacks

  // Only numbers entered on this card must differ; the login number may belong to a member
  // (e.g. a spouse logged in on their phone and lists the user under Self with another number).
  const errors = validate(members, [self.mobile]);
  const selfErrors = {
    name: NAME_REGEX.test(self.name.trim()) ? '' : 'Enter your name',
    mobile: MOBILE_REGEX.test(self.mobile) ? '' : 'Enter a valid 10-digit mobile number',
    pan: self.pan && !PAN_REGEX.test(self.pan) ? 'Enter a valid PAN (e.g. ABCDE1234F)' : '',
  };
  const valid = !loading && !selfErrors.name && !selfErrors.mobile && !selfErrors.pan
    && errors.every((e) => !e.name && !e.mobile && !e.pan);
  latestRef.current = { members, self, valid, loaded: !loading && !loadError };

  useEffect(() => {
    if (state.familyMembersValid !== valid) actions.updateFormData('familyMembersValid', valid);
  }, [valid]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!loading && state.familyMemberCount !== members.length) {
      actions.updateFormData('familyMemberCount', members.length); // adds the family wealth card
    }
  }, [loading, members.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Save on leaving the card (Next or Back); WealthConnect waits for this before loading.
  useEffect(() => () => {
    const { members: latest, self: me, valid: ok, loaded } = latestRef.current;
    if (!ok || !loaded) return;
    const payload = latest.map((m) => ({ name: m.name.trim(), mobile: m.mobile, pan: m.pan || null }));
    const own = { name: me.name.trim(), mobile: me.mobile, pan: me.pan || null };
    queueFamilySave(() => wealthService.saveFamily(payload, own))
      .catch((err) => console.error('Saving family members failed:', err));
  }, []);

  const setCount = (_, count) => {
    setMembers((prev) => (count > prev.length
      ? [...prev, ...Array.from({ length: count - prev.length }, () => ({ name: '', mobile: '', pan: '' }))]
      : prev.slice(0, count)));
  };

  const update = (index, field, value) => {
    setMembers((prev) => prev.map((m, i) => (i === index ? { ...m, [field]: value } : m)));
  };

  const touch = (index, field) => () => setTouched((t) => ({ ...t, [`${index}:${field}`]: true }));

  const showError = (index, field) => {
    const message = errors[index]?.[field];
    const duplicate = field === 'mobile' && message?.includes('already');
    return message && (touched[`${index}:${field}`] || duplicate) ? message : '';
  };

  if (loading) return <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>;

  const helper = { FormHelperTextProps: { sx: { minHeight: 20, ml: 0 } } };
  const selfError = (field) => (selfErrors[field] && (selfTouched[field] || field === 'pan') ? selfErrors[field] : '');
  const touchSelf = (field) => () => setSelfTouched((t) => ({ ...t, [field]: true }));

  return (
    <CardContent>
      <Typography variant="h6" sx={{ mb: 1, color: 'primary.main' }}>
        Your Household Investments
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Select the number of all family members who have any financial investments.
      </Typography>
      {loadError && <Alert severity="warning" sx={{ my: 1 }}>{loadError}</Alert>}

      <Slider
        value={members.length}
        onChange={setCount}
        step={1}
        marks={[
          { value: 0, label: 'Self' },
          { value: 1, label: '1' },
          { value: 2, label: '2' },
          { value: 3, label: '3' },
        ]}
        min={0}
        max={MAX_MEMBERS}
        valueLabelDisplay="auto"
        getAriaValueText={(v) => (v === 0 ? 'Self only' : `${v} family members`)}
        aria-label="Number of family members besides you"
        sx={{ mr: 2, flexGrow: 1, '& .MuiSlider-valueLabelCircle': { backgroundColor: '#757575' } }}
      />

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
        We use your PAN and mobile to fetch investments from Account Aggregators.
      </Typography>

      <Box sx={{ mt: 1.5, mb: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>Self</Typography>
        <TextField
          label="Name"
          value={self.name}
          onChange={(e) => setSelf((p) => ({ ...p, name: e.target.value.slice(0, 100) }))}
          onBlur={touchSelf('name')}
          size="small"
          fullWidth
          error={Boolean(selfError('name'))}
          helperText={selfError('name') || ' '}
          inputProps={{ autoComplete: 'name', 'aria-label': 'Your name' }}
          {...helper}
        />
        <Grid container spacing={2} wrap="nowrap" alignItems="flex-start">
          <Grid item xs={6}>
            <TextField
              label="Mobile number"
              value={self.mobile}
              onChange={(e) => setSelf((p) => ({ ...p, mobile: toTenDigits(e.target.value) }))}
              onBlur={touchSelf('mobile')}
              size="small"
              fullWidth
              error={Boolean(selfError('mobile'))}
              helperText={selfError('mobile') || ' '}
              InputProps={{ startAdornment: <InputAdornment position="start">+91</InputAdornment> }}
              inputProps={{ inputMode: 'numeric', autoComplete: 'tel-national', 'aria-label': 'Your mobile number' }}
              {...helper}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="PAN"
              value={self.pan}
              onChange={(e) => setSelf((p) => ({ ...p, pan: cleanPan(e.target.value) }))}
              size="small"
              fullWidth
              error={Boolean(selfError('pan'))}
              helperText={selfError('pan') || ' '}
              inputProps={{ autoCapitalize: 'characters', autoComplete: 'off', 'aria-label': 'Your PAN' }}
              {...helper}
            />
          </Grid>
        </Grid>
      </Box>

      {members.map((member, index) => (
        <Box key={index} sx={{ mb: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>Member {index + 1}</Typography>
          <TextField
            label="Name"
            value={member.name}
            onChange={(e) => update(index, 'name', e.target.value.slice(0, 100))}
            onBlur={touch(index, 'name')}
            size="small"
            fullWidth
            error={Boolean(showError(index, 'name'))}
            helperText={showError(index, 'name') || ' '}
            inputProps={{ autoComplete: 'off', 'aria-label': `Member ${index + 1} name` }}
            {...helper}
          />
          <Grid container spacing={2} wrap="nowrap" alignItems="flex-start">
            <Grid item xs={6}>
              <TextField
                label="Mobile number"
                value={member.mobile}
                onChange={(e) => update(index, 'mobile', toTenDigits(e.target.value))}
                onBlur={touch(index, 'mobile')}
                size="small"
                fullWidth
                error={Boolean(showError(index, 'mobile'))}
                helperText={showError(index, 'mobile') || ' '}
                InputProps={{ startAdornment: <InputAdornment position="start">+91</InputAdornment> }}
                inputProps={{ inputMode: 'numeric', autoComplete: 'off', 'aria-label': `Member ${index + 1} mobile number` }}
                {...helper}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="PAN"
                value={member.pan}
                onChange={(e) => update(index, 'pan', cleanPan(e.target.value))}
                onBlur={touch(index, 'pan')}
                size="small"
                fullWidth
                error={Boolean(showError(index, 'pan'))}
                helperText={showError(index, 'pan') || ' '}
                inputProps={{ autoCapitalize: 'characters', autoComplete: 'off', 'aria-label': `Member ${index + 1} PAN` }}
                {...helper}
              />
            </Grid>
          </Grid>
        </Box>
      ))}
    </CardContent>
  );
};

export default FamilyMembers;
